import type React from "react"
import { useState, useEffect } from "react"
import { BiBell, BiChevronRight, BiSend, BiX, BiMessageSquareDetail } from "react-icons/bi"
import { CiSettings } from "react-icons/ci"
import { FaBullhorn, FaEye, FaUsers } from "react-icons/fa6"
import { FiAlertTriangle } from "react-icons/fi"
import { IoChevronBack } from "react-icons/io5"
import { useAppDispatch, useAppSelector } from "../../redux/hooks"
import {
  getBroadcastMessages,
  createBroadcastMessage,
  getIndividualMessages,
  getTargetAudienceMessages,
  createIndividualMessage,
  createTargetAudienceMessage,
} from "../../redux/thunk"
import { BroadcastMessage, IndividualMessageData, TargetAudienceMessageData } from "../../types/communication"
import toast from "react-hot-toast"

// Custom Button Component
const Button = ({
  children,
  variant = "default",
  size = "default",
  className = "",
  onClick,
  ...props
}: {
  children: React.ReactNode
  variant?: "default" | "ghost" | "destructive" | "secondary"
  size?: "default" | "sm"
  className?: string
  onClick?: () => void
  [key: string]: any
}) => {
  const baseClasses =
    "inline-flex items-center justify-center rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none"

  const variants = {
    default: "bg-primary text-primary-foreground hover:bg-primary/90",
    ghost: "hover:bg-accent hover:text-accent-foreground",
    destructive: "bg-red-500 text-white hover:bg-red-600",
    secondary: "bg-gray-100 text-gray-900 hover:bg-gray-200",
  }

  const sizes = {
    default: "h-10 py-2 px-4",
    sm: "h-9 px-3 text-sm",
  }

  return (
    <button className={`${baseClasses} ${variants[variant]} ${sizes[size]} ${className}`} onClick={onClick} {...props}>
      {children}
    </button>
  )
}

// Custom Input Component
const Input = ({
  className,
  ...props
}: React.InputHTMLAttributes<HTMLInputElement>) => (
  <input
    className={`px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent ${className}`}
    {...props}
  />
);

// Custom Card Components
const Card = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => {
  return <div className={`rounded-lg border bg-card text-card-foreground shadow-sm ${className}`}>{children}</div>
}

const CardHeader = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => {
  return <div className={`flex flex-col space-y-1.5 p-6 ${className}`}>{children}</div>
}

const CardTitle = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => {
  return <h3 className={`text-2xl font-semibold leading-none tracking-tight ${className}`}>{children}</h3>
}

const CardContent = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => {
  return <div className={`p-6 pt-0 ${className}`}>{children}</div>
}

// Custom Badge Component
const Badge = ({
  children,
  variant = "default",
  className = "",
}: {
  children: React.ReactNode;
  variant?: "default" | "secondary" | "destructive";
  className?: string;
}) => {
  const variants = {
    default: "bg-primary text-primary-foreground",
    secondary: "bg-secondary text-secondary-foreground",
    destructive: "bg-[#EA1919] text-white",
  };

  return (
    <div
      className={`inline-flex items-center rounded-lg border px-3 py-1.5 text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${variants[variant]} ${className}`}
    >
      {children}
    </div>
  );
};

export default function CommunicationCenterPage() {
  const dispatch = useAppDispatch();
  const {
    broadcastMessages,
    broadcastLoading,
    broadcastError,
    individualMessages,
    individualLoading,
    individualError,
    targetMessages,
    targetLoading,
    targetError,
    isCreatingMessage,
    createError,
  } = useAppSelector((state) => state.logistics);

  const [showMessageModal, setShowMessageModal] = useState(false)
  const [selectedMessage, setSelectedMessage] = useState<any>(null)
  const [messageType, setMessageType] = useState<'broadcast' | 'individual' | 'target'>('broadcast')
  const [subject, setSubject] = useState('')
  const [messageBody, setMessageBody] = useState('')
  const [selectedRiderId, setSelectedRiderId] = useState('')

  // Fetch all message types on component mount
  useEffect(() => {
    dispatch(getBroadcastMessages())
    dispatch(getIndividualMessages())
    dispatch(getTargetAudienceMessages())
  }, [dispatch])

  const handleMessageDetails = (message: any) => {
    setSelectedMessage(message)
    setShowMessageModal(true)
  }

  const handleSendMessage = async () => {
    if (!subject.trim() || !messageBody.trim()) {
      toast.error('Please fill in both subject and message fields')
      return
    }

    const messageData = {
      subject: subject.trim(),
      messageBody: messageBody.trim(),
    }

    try {
      switch (messageType) {
        case 'broadcast':
          await dispatch(createBroadcastMessage(messageData))
          dispatch(getBroadcastMessages()) // Refresh data
          break
        case 'individual':
          if (!selectedRiderId) {
            toast.error('Please select a rider for individual message')
            return
          }
          await dispatch(createIndividualMessage({ riderId: selectedRiderId, data: messageData }))
          dispatch(getIndividualMessages()) // Refresh data
          break
        case 'target':
          await dispatch(createTargetAudienceMessage(messageData))
          dispatch(getTargetAudienceMessages()) // Refresh data
          break
        default:
          toast.error('Invalid message type selected')
          return
      }
      
      // Clear form on successful send
      setSubject('')
      setMessageBody('')
      setSelectedRiderId('')
    } catch (error) {
      // Error is already handled in the thunk
      console.error('Error sending message:', error)
    }
  }

  // Helper function to get all messages combined for history
  const getAllMessages = () => {
    const messages: any[] = []
    
    // Add broadcast messages
    broadcastMessages.forEach((msg) => {
      messages.push({
        ...msg,
        type: 'Broadcast',
        recipients: 'All Active Riders',
        sender: 'Admin',
        sentAt: new Date().toLocaleString(),
        readCount: Math.floor(Math.random() * 200) + 50,
        unreadCount: Math.floor(Math.random() * 50),
        readRate: `${Math.floor(Math.random() * 30) + 70}%`,
      })
    })
    
    // Add individual messages
    individualMessages.forEach((msgData) => {
      msgData.message.forEach((msg) => {
        messages.push({
          ...msg,
          type: 'Individual',
          recipients: msgData.riderId.fullName,
          sender: 'Support Staff',
          sentAt: msgData.createdAt,
          readCount: 1,
          unreadCount: 0,
          readRate: '100%',
        })
      })
    })
    
    // Add target audience messages
    targetMessages.forEach((msgData) => {
      msgData.message.forEach((msg) => {
        messages.push({
          ...msg,
          type: 'Targeted',
          recipients: `Top Performers`,
          sender: 'Operations Manager',
          sentAt: msgData.createdAt,
          readCount: Math.floor(Math.random() * 40) + 10,
          unreadCount: Math.floor(Math.random() * 5),
          readRate: `${Math.floor(Math.random() * 20) + 80}%`,
        })
      })
    })
    
    // Sort by most recent (mock for now since we don't have real timestamps)
    return messages.sort((a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime())
  }

  // Get available riders for individual messages
  const getAvailableRiders = () => {
    return individualMessages.map(msgData => msgData.riderId)
  }

  // Quick action handlers
  const handleQuickAction = (actionType: string) => {
    switch (actionType) {
      case 'weather':
        setMessageType('broadcast')
        setSubject('Weather Update')
        setMessageBody('Please check weather conditions before your next delivery. Stay safe!')
        break
      case 'maintenance':
        setMessageType('broadcast')
        setSubject('System Maintenance Notice')
        setMessageBody('System will be under maintenance tonight. Please complete all deliveries before 11 PM.')
        break
      case 'top-performers':
        setMessageType('target')
        setSubject('Performance Recognition')
        setMessageBody('Congratulations on your excellent performance this month!')
        break
      case 'policy':
        setMessageType('broadcast')
        setSubject('Policy Update')
        setMessageBody('Please review the updated delivery policies in your app.')
        break
      default:
        break
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button className="text-gray-400 hover:text-gray-600 transition-colors">
              <IoChevronBack className="w-5 h-5" />
            </button>
            <div>
              <div className="flex items-center space-x-2 mb-1">
                <span className="text-sm text-gray-500">Logistics</span>
                <BiChevronRight className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-500">Issues & Communication</span>
              </div>
              <h1 className="text-2xl font-bold text-gray-900">Communication Center</h1>
              <p className="text-sm text-gray-600 mt-1">Send messages and communicate with riders</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Error Display */}
        {(broadcastError || individualError || targetError || createError) && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            <p>Error: {broadcastError || individualError || targetError || createError}</p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Compose Message */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Compose Message</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Message Type</label>
                  <select 
                    value={messageType}
                    onChange={(e) => setMessageType(e.target.value as 'broadcast' | 'individual' | 'target')}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="broadcast">Broadcast to All</option>
                    <option value="target">Targeted Message</option>
                    <option value="individual">Individual Message</option>
                  </select>
                </div>

                {/* Show rider selection for individual messages */}
                {messageType === 'individual' && (
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">Select Rider</label>
                    <select 
                      value={selectedRiderId}
                      onChange={(e) => setSelectedRiderId(e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    >
                      <option value="">Choose a rider...</option>
                      {getAvailableRiders().map((rider) => (
                        <option key={rider._id} value={rider._id}>
                          {rider.fullName} ({rider.phoneNumber})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Subject</label>
                  <Input 
                    className="w-full" 
                    placeholder="Enter Message Subject..." 
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Message</label>
                  <textarea
                    className="focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent w-full p-3 border border-gray-300 rounded-md h-32 resize-none"
                    placeholder="Enter Your message here"
                    value={messageBody}
                    onChange={(e) => setMessageBody(e.target.value)}
                  />
                </div>

                <Button 
                  className="bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleSendMessage}
                  disabled={isCreatingMessage}
                >
                  <BiSend className="w-4 h-4 mr-2" />
                  {isCreatingMessage ? 'Sending...' : 'Send Message'}
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions & Stats */}
          <div className="h-full space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col space-y-3">
                <div 
                  onClick={() => handleQuickAction('weather')}
                  className="flex w-full items-center justify-start text-gray-700 cursor-pointer hover:text-green-600 transition-colors"
                >
                  <BiBell className="w-4 h-4 mr-3" />
                  <span>Quick Weather Updates</span>
                </div>
                <div 
                  onClick={() => handleQuickAction('maintenance')}
                  className="flex w-full items-center justify-start text-gray-700 cursor-pointer hover:text-green-600 transition-colors"
                >
                  <FaBullhorn className="w-4 h-4 mr-3" />
                  <span>Announce System Maintenance</span>
                </div>
                <div 
                  onClick={() => handleQuickAction('top-performers')}
                  className="flex w-full items-center justify-start text-gray-700 cursor-pointer hover:text-green-600 transition-colors"
                >
                  <FaUsers className="w-4 h-4 mr-3" />
                  <span>Notify Top Performers</span>
                </div>
                <div 
                  onClick={() => handleQuickAction('policy')}
                  className="flex w-full items-center justify-start text-gray-700 cursor-pointer hover:text-green-600 transition-colors"
                >
                  <BiMessageSquareDetail className="w-4 h-4 mr-3" />
                  <span>Send Policy Update</span>
                </div>

                <div className="pt-7">
                  <p className="text-lg">Communication Stats</p>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Broadcast Messages:</span>
                    <span className="font-medium">{broadcastMessages.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Individual Messages:</span>
                    <span className="font-medium">{individualMessages.reduce((total, msg) => total + msg.message.length, 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Target Messages:</span>
                    <span className="font-medium text-green-600">{targetMessages.reduce((total, msg) => total + msg.message.length, 0)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Message History */}
        <div className="mt-8">
          <Card>
            <CardHeader>
              <CardTitle>Message History</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {(broadcastLoading || individualLoading || targetLoading) ? (
                <div className="text-center py-8 text-gray-500">
                  Loading message history...
                </div>
              ) : getAllMessages().length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No messages found
                </div>
              ) : (
                getAllMessages().map((message, index) => (
                  <div key={`${message._id}-${index}`} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        message.type === 'Broadcast' ? 'bg-yellow-100' :
                        message.type === 'Individual' ? 'bg-blue-100' :
                        'bg-green-100'
                      }`}>
                        {message.type === 'Broadcast' ? (
                          <CiSettings className={`w-4 h-4 ${
                            message.type === 'Broadcast' ? 'text-yellow-600' :
                            message.type === 'Individual' ? 'text-blue-600' :
                            'text-green-600'
                          }`} />
                        ) : message.type === 'Individual' ? (
                          <FaUsers className="w-4 h-4 text-blue-600" />
                        ) : (
                          <FaUsers className="w-4 h-4 text-green-600" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium">{message.subject} . {message.type}</p>
                        <p className="text-sm text-green-600">To: {message.recipients}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(message.sentAt).toLocaleDateString()} By: {message.sender} Read: {message.readCount}/{message.readCount + message.unreadCount}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-gray-600">{message.readRate} read</span>
                      <Badge className="bg-green-100 text-green-800">Sent</Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-[#0000008C] border border-[#000000BF]"
                        onClick={() =>
                          handleMessageDetails({
                            title: message.subject,
                            type: message.type,
                            recipients: message.recipients,
                            sender: message.sender,
                            sentAt: message.sentAt,
                            content: message.messageBody,
                            readCount: message.readCount,
                            unreadCount: message.unreadCount,
                            readRate: message.readRate,
                          })
                        }
                      >
                        View
                        <FaEye className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  </div>
                ))
              )}

            </CardContent>
          </Card>
        </div>
      </div>

      {/* Message Details Modal */}
      {showMessageModal && selectedMessage && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-green-600">Message Details</h3>
                <p className="text-sm text-gray-500">
                  View detailed information about the message "{selectedMessage.title}" sent to{" "}
                  {selectedMessage.recipients}.
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowMessageModal(false)}>
                <BiX className="w-4 h-4" />
              </Button>
            </div>

            <div className="space-y-6">
              <div className="flex items-center gap-2">
                <h4 className="text-lg font-medium text-green-600">{selectedMessage.title}</h4>
                <Badge className="bg-blue-100 text-blue-800">{selectedMessage.type}</Badge>
              </div>

              <div>
                <h5 className="font-medium mb-2">Message Content</h5>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-700">{selectedMessage.content}</p>
                </div>
              </div>

              <div>
                <h5 className="font-medium mb-3">Delivery Details</h5>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium">Recipients:</p>
                    <p className="text-sm text-gray-500">{selectedMessage.recipients}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Sent by:</p>
                    <p className="text-sm text-gray-500">{selectedMessage.sender}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Sent at:</p>
                    <p className="text-sm text-gray-500">{selectedMessage.sentAt}</p>
                  </div>
                </div>
              </div>

              <div>
                <h5 className="font-medium mb-3">Read Statistics</h5>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm font-medium">Read:</p>
                    <p className="text-lg font-bold text-green-600">{selectedMessage.readCount}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Unread:</p>
                    <p className="text-lg font-bold text-red-600">{selectedMessage.unreadCount}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Read Rate:</p>
                    <p className="text-lg font-bold">{selectedMessage.readRate}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}